import { useEffect, useState } from 'react';

import { useGroups } from '../../../hooks/useGroups';
import usePermissions from '../../../hooks/usePermissions';
import { usePaginatedFilter } from '../../../common/hooks/usePaginatedFilter';
import {
  useConfirmPuntajePreAdjudication,
  usePuntajeContractDebtDetail,
  usePuntajeContractDetail,
  usePuntajeCouponDetail,
  usePuntajeDashboard,
  usePuntajeEligibility,
  usePuntajeFundDetails,
  usePuntajeInitialBalances,
  usePuntajeLotteryResults,
  usePuntajePreAdjudications,
  usePuntajeRanking,
  useDeletePuntajeInitialBalance,
  useSyncPuntajeLotteryResult,
  useUpsertPuntajeInitialBalance,
  useUpsertPuntajeLotteryResult,
} from '../../../hooks/usePuntaje';
import { DEFAULT_PERIOD } from '../constants/puntajeConstants';
import { shouldDisplayRankingRow } from '../utils/puntajeFilters';
import { getErrorMessage } from '../utils/puntajeFormatters';

const DEFAULT_TABLE_ITEMS = 10;
const identityFilter = (collection) => collection;
const buildInitialBalanceForm = (selectedGroupUuid, overrides = {}) => ({
  uuid: '',
  year: DEFAULT_PERIOD.year,
  uuid_group: selectedGroupUuid || '',
  fund_type: 'PUNTAJE',
  installments_balance: '0',
  notes: '',
  ...overrides,
});

/**
 * Orquesta estado de vista, queries y acciones del feature Puntaje.
 *
 * @returns {Object} Estado, datos derivados, queries, mutaciones y handlers del feature.
 */
export const usePuntajePage = () => {
  const { data: groups = [], isLoading: loadingGroups } = useGroups();
  const { hasAnyModule, permissions } = usePermissions();
  const isAdmin = permissions.isAdmin;
  const canSeePuntaje = hasAnyModule(['Puntaje', 'Habitat 99']);

  const [activeTab, setActiveTab] = useState(0);
  const [activeRankingTab, setActiveRankingTab] = useState(0);
  const [activePreAdjudicationsTab, setActivePreAdjudicationsTab] = useState(0);
  const [selectedGroupUuid, setSelectedGroupUuid] = useState('');
  const [year, setYear] = useState(DEFAULT_PERIOD.year);
  const [month, setMonth] = useState(DEFAULT_PERIOD.month);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: 'success',
    message: '',
  });
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    suggestion: null,
    observations: '',
  });
  const [lotteryForm, setLotteryForm] = useState({
    year: DEFAULT_PERIOD.year,
    month: DEFAULT_PERIOD.month,
    draw_date: '',
    full_number: '',
    source_url: '',
    screenshot_url: '',
    observations: '',
  });
  const [initialBalanceForm, setInitialBalanceForm] = useState({
    ...buildInitialBalanceForm(''),
  });
  const [initialBalanceDialogOpen, setInitialBalanceDialogOpen] = useState(false);
  const [initialBalanceDeleteDialog, setInitialBalanceDeleteDialog] = useState({
    open: false,
    row: null,
  });
  const [contractDebtDialog, setContractDebtDialog] = useState({
    open: false,
    row: null,
  });
  const [couponDetailDialog, setCouponDetailDialog] = useState({
    open: false,
    uuid: null,
  });
  const [contractDetailDialog, setContractDetailDialog] = useState({
    open: false,
    uuid: null,
  });

  useEffect(() => {
    if (!selectedGroupUuid && groups.length) {
      setSelectedGroupUuid(groups[0].uuid);
    }
  }, [groups, selectedGroupUuid]);

  useEffect(() => {
    setLotteryForm((prev) => ({
      ...prev,
      year,
      month,
    }));
  }, [year, month]);

  useEffect(() => {
    if (!initialBalanceForm.uuid_group && selectedGroupUuid) {
      setInitialBalanceForm((prev) => ({ ...prev, uuid_group: selectedGroupUuid }));
    }
  }, [initialBalanceForm.uuid_group, selectedGroupUuid]);

  const dashboardQuery = usePuntajeDashboard({ year, month });
  const rankingQuery = usePuntajeRanking(selectedGroupUuid, { page: 1, per_page: 200 });
  const eligibilityQuery = usePuntajeEligibility(selectedGroupUuid, { page: 1, per_page: 200 });
  const puntajeFundQuery = usePuntajeFundDetails(selectedGroupUuid, 'PUNTAJE', { year, month });
  const sorteoFundQuery = usePuntajeFundDetails(selectedGroupUuid, 'SORTEO', { year, month });
  const preAdjudicationsQuery = usePuntajePreAdjudications(selectedGroupUuid, {
    page: 1,
    per_page: 200,
  });
  const lotteryResultsQuery = usePuntajeLotteryResults({
    year,
    month,
    page: 1,
    per_page: 200,
  });
  const initialBalancesQuery = usePuntajeInitialBalances({
    uuid_group: selectedGroupUuid,
  });
  const contractDebtDetailQuery = usePuntajeContractDebtDetail(contractDebtDialog.row?.uuid_contract, {
    enabled: contractDebtDialog.open && !!contractDebtDialog.row?.uuid_contract,
  });
  const couponDetailQuery = usePuntajeCouponDetail(couponDetailDialog.uuid, {
    enabled: couponDetailDialog.open && !!couponDetailDialog.uuid,
  });
  const contractDetailQuery = usePuntajeContractDetail(contractDetailDialog.uuid, {
    enabled: contractDetailDialog.open && !!contractDetailDialog.uuid,
  });

  const confirmMutation = useConfirmPuntajePreAdjudication();
  const syncLotteryMutation = useSyncPuntajeLotteryResult();
  const upsertLotteryMutation = useUpsertPuntajeLotteryResult();
  const upsertInitialBalanceMutation = useUpsertPuntajeInitialBalance();
  const deleteInitialBalanceMutation = useDeletePuntajeInitialBalance();

  const selectedGroup = groups.find((group) => group.uuid === selectedGroupUuid) || null;
  const dashboardRows = dashboardQuery.data || [];
  const selectedDashboard =
    dashboardRows.find((dashboard) => dashboard.uuid_group === selectedGroupUuid) || null;
  const rankingRows = (rankingQuery.data?.results || []).filter(shouldDisplayRankingRow);
  const eligibilityRows = (eligibilityQuery.data?.results || []).filter(shouldDisplayRankingRow);
  const puntajeFund = puntajeFundQuery.data || { summary: null, movements: [] };
  const sorteoFund = sorteoFundQuery.data || { summary: null, movements: [] };
  const preAdjudicationsRows = preAdjudicationsQuery.data?.results || [];
  const lotteryRows = lotteryResultsQuery.data?.results || [];
  const initialBalanceRows = initialBalancesQuery.data || [];

  const dashboardPagination = usePaginatedFilter({
    data: dashboardRows,
    itemsPerPage: DEFAULT_TABLE_ITEMS,
    filterFn: identityFilter,
  });
  const rankingPagination = usePaginatedFilter({
    data: rankingRows,
    itemsPerPage: DEFAULT_TABLE_ITEMS,
    filterFn: identityFilter,
  });
  const eligibilityPagination = usePaginatedFilter({
    data: eligibilityRows,
    itemsPerPage: DEFAULT_TABLE_ITEMS,
    filterFn: identityFilter,
  });
  const puntajeMovementsPagination = usePaginatedFilter({
    data: puntajeFund.movements || [],
    itemsPerPage: DEFAULT_TABLE_ITEMS,
    filterFn: identityFilter,
  });
  const sorteoMovementsPagination = usePaginatedFilter({
    data: sorteoFund.movements || [],
    itemsPerPage: DEFAULT_TABLE_ITEMS,
    filterFn: identityFilter,
  });
  const puntajePreAdjudicationsPagination = usePaginatedFilter({
    data: preAdjudicationsRows.filter((row) => row.pre_adjudication_type === 'PUNTAJE'),
    itemsPerPage: DEFAULT_TABLE_ITEMS,
    filterFn: identityFilter,
  });
  const sorteoPreAdjudicationsPagination = usePaginatedFilter({
    data: preAdjudicationsRows.filter((row) => row.pre_adjudication_type === 'SORTEO'),
    itemsPerPage: DEFAULT_TABLE_ITEMS,
    filterFn: identityFilter,
  });
  const desbordePreAdjudicationsPagination = usePaginatedFilter({
    data: preAdjudicationsRows.filter((row) => row.pre_adjudication_type === 'DESBORDE'),
    itemsPerPage: DEFAULT_TABLE_ITEMS,
    filterFn: identityFilter,
  });
  const lotteryPagination = usePaginatedFilter({
    data: lotteryRows,
    itemsPerPage: DEFAULT_TABLE_ITEMS,
    filterFn: identityFilter,
  });
  const initialBalancesPagination = usePaginatedFilter({
    data: initialBalanceRows,
    itemsPerPage: DEFAULT_TABLE_ITEMS,
    filterFn: identityFilter,
  });

  const financingInstallmentsCreated = preAdjudicationsRows.reduce(
    (accumulator, row) => accumulator + Number(row.financing_installments_created || 0),
    0
  );

  const handleOpenSnackbar = (severity, message) => {
    setSnackbar({
      open: true,
      severity,
      message,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const handleOpenConfirmation = (suggestion) => {
    setConfirmationDialog({
      open: true,
      suggestion,
      observations: '',
    });
  };

  const handleCloseConfirmation = () => {
    setConfirmationDialog({
      open: false,
      suggestion: null,
      observations: '',
    });
  };

  const handleConfirmPreAdjudication = async () => {
    if (!confirmationDialog.suggestion || !selectedGroupUuid) return;

    try {
      await confirmMutation.mutateAsync({
        uuid_group: selectedGroupUuid,
        uuid_contract: confirmationDialog.suggestion.uuid_contract,
        pre_adjudication_type: confirmationDialog.suggestion.suggestion_type,
        year,
        month,
        observations: confirmationDialog.observations || undefined,
      });

      handleCloseConfirmation();
      handleOpenSnackbar('success', 'Preadjudicacion confirmada correctamente.');
    } catch (error) {
      handleOpenSnackbar(
        'error',
        getErrorMessage(error, 'No se pudo confirmar la preadjudicacion.')
      );
    }
  };

  const handleSaveLotteryResult = async () => {
    try {
      await upsertLotteryMutation.mutateAsync({
        year: Number(lotteryForm.year),
        month: Number(lotteryForm.month),
        draw_date: lotteryForm.draw_date,
        full_number: lotteryForm.full_number,
        source_url: lotteryForm.source_url || undefined,
        screenshot_url: lotteryForm.screenshot_url || undefined,
        observations: lotteryForm.observations || undefined,
      });

      handleOpenSnackbar('success', 'Resultado de loteria guardado correctamente.');
    } catch (error) {
      handleOpenSnackbar('error', getErrorMessage(error, 'No se pudo guardar la loteria.'));
    }
  };

  const handleSyncLotteryResult = async () => {
    try {
      await syncLotteryMutation.mutateAsync({
        year,
        month,
      });

      handleOpenSnackbar('success', 'Resultado oficial sincronizado correctamente.');
    } catch (error) {
      handleOpenSnackbar(
        'error',
        getErrorMessage(error, 'No se pudo sincronizar la loteria oficial.')
      );
    }
  };

  const handleSaveInitialBalance = async () => {
    try {
      await upsertInitialBalanceMutation.mutateAsync({
        uuid: initialBalanceForm.uuid || undefined,
        year: Number(initialBalanceForm.year),
        uuid_group: initialBalanceForm.uuid_group,
        fund_type: initialBalanceForm.fund_type,
        installments_balance: Number(initialBalanceForm.installments_balance || 0),
        notes: initialBalanceForm.notes || undefined,
      });

      setInitialBalanceDialogOpen(false);
      setInitialBalanceForm(buildInitialBalanceForm(selectedGroupUuid));
      handleOpenSnackbar(
        'success',
        initialBalanceForm.uuid
          ? 'Saldo inicial actualizado correctamente.'
          : 'Saldo inicial guardado correctamente.'
      );
    } catch (error) {
      handleOpenSnackbar('error', getErrorMessage(error, 'No se pudo guardar el saldo inicial.'));
    }
  };

  const handleEditInitialBalance = (row) => {
    setInitialBalanceForm({
      uuid: row.uuid,
      year: row.year,
      uuid_group: row.uuid_group,
      fund_type: row.fund_type,
      installments_balance: String(row.installments_balance ?? 0),
      notes: row.notes || '',
    });
    setInitialBalanceDialogOpen(true);
  };

  const handleOpenInitialBalanceDialog = () => {
    setInitialBalanceForm(buildInitialBalanceForm(selectedGroupUuid));
    setInitialBalanceDialogOpen(true);
  };

  const handleCloseInitialBalanceDialog = () => {
    setInitialBalanceDialogOpen(false);
    setInitialBalanceForm(buildInitialBalanceForm(selectedGroupUuid));
  };

  const handleOpenInitialBalanceDelete = (row) => {
    setInitialBalanceDeleteDialog({
      open: true,
      row,
    });
  };

  const handleCloseInitialBalanceDelete = () => {
    setInitialBalanceDeleteDialog({
      open: false,
      row: null,
    });
  };

  const handleDeleteInitialBalance = async () => {
    if (!initialBalanceDeleteDialog.row?.uuid) return;

    try {
      await deleteInitialBalanceMutation.mutateAsync(initialBalanceDeleteDialog.row.uuid);
      if (initialBalanceForm.uuid === initialBalanceDeleteDialog.row.uuid) {
        setInitialBalanceForm(buildInitialBalanceForm(selectedGroupUuid));
      }
      handleCloseInitialBalanceDelete();
      handleOpenSnackbar('success', 'Saldo inicial eliminado correctamente.');
    } catch (error) {
      handleOpenSnackbar('error', getErrorMessage(error, 'No se pudo eliminar el saldo inicial.'));
    }
  };

  const handleOpenContractDebtDetail = (row) => {
    setContractDebtDialog({
      open: true,
      row,
    });
  };

  const handleCloseContractDebtDetail = () => {
    setContractDebtDialog({
      open: false,
      row: null,
    });
  };

  const handleOpenCouponDetail = (movement) => {
    if (movement.uuid_coupon) {
      setCouponDetailDialog({ open: true, uuid: movement.uuid_coupon });
    }
  };

  const handleCloseCouponDetail = () => {
    setCouponDetailDialog({ open: false, uuid: null });
  };

  const handleOpenContractDetail = (movement) => {
    if (movement.uuid_contract) {
      setContractDetailDialog({ open: true, uuid: movement.uuid_contract });
    }
  };

  const handleCloseContractDetail = () => {
    setContractDetailDialog({ open: false, uuid: null });
  };

  const handleRefreshVisibleData = async () => {
    await Promise.all([
      dashboardQuery.refetch(),
      rankingQuery.refetch(),
      eligibilityQuery.refetch(),
      puntajeFundQuery.refetch(),
      sorteoFundQuery.refetch(),
      preAdjudicationsQuery.refetch(),
      lotteryResultsQuery.refetch(),
      initialBalancesQuery.refetch(),
    ]);
  };

  const handleSelectGroup = (uuidGroup) => {
    setSelectedGroupUuid(uuidGroup);
    setInitialBalanceForm((prev) => ({
      ...prev,
      uuid_group: uuidGroup,
    }));
  };

  return {
    permissions: {
      isAdmin,
      canSeePuntaje,
    },
    filters: {
      groups,
      loadingGroups,
      selectedGroupUuid,
      selectedGroup,
      year,
      month,
      activeTab,
      activeRankingTab,
      activePreAdjudicationsTab,
      setYear,
      setMonth,
      setActiveTab,
      setActiveRankingTab,
      setActivePreAdjudicationsTab,
      handleSelectGroup,
    },
    dialogs: {
      snackbar,
      confirmationDialog,
      lotteryForm,
      initialBalanceDialogOpen,
      initialBalanceForm,
      initialBalanceDeleteDialog,
      contractDebtDialog,
      couponDetailDialog,
      contractDetailDialog,
      setConfirmationDialog,
      setLotteryForm,
      setInitialBalanceForm,
      setInitialBalanceDeleteDialog,
      handleCloseSnackbar,
      handleOpenConfirmation,
      handleCloseConfirmation,
      handleCloseInitialBalanceDialog,
      handleCloseInitialBalanceDelete,
      handleCloseContractDebtDetail,
      handleCloseCouponDetail,
      handleCloseContractDetail,
    },
    data: {
      dashboardRows,
      selectedDashboard,
      rankingRows,
      eligibilityRows,
      puntajeFund,
      sorteoFund,
      preAdjudicationsRows,
      lotteryRows,
      initialBalanceRows,
      financingInstallmentsCreated,
      contractDebtDetail: contractDebtDetailQuery.data || null,
      couponDetail: couponDetailQuery.data || null,
      contractDetail: contractDetailQuery.data || null,
    },
    paginations: {
      dashboardPagination,
      rankingPagination,
      eligibilityPagination,
      puntajeMovementsPagination,
      sorteoMovementsPagination,
      puntajePreAdjudicationsPagination,
      sorteoPreAdjudicationsPagination,
      desbordePreAdjudicationsPagination,
      lotteryPagination,
      initialBalancesPagination,
    },
    queries: {
      dashboardQuery,
      rankingQuery,
      eligibilityQuery,
      puntajeFundQuery,
      sorteoFundQuery,
      preAdjudicationsQuery,
      lotteryResultsQuery,
      initialBalancesQuery,
      contractDebtDetailQuery,
      couponDetailQuery,
      contractDetailQuery,
    },
    mutations: {
      confirmMutation,
      syncLotteryMutation,
      upsertLotteryMutation,
      upsertInitialBalanceMutation,
      deleteInitialBalanceMutation,
    },
    handlers: {
      handleConfirmPreAdjudication,
      handleSaveLotteryResult,
      handleSyncLotteryResult,
      handleSaveInitialBalance,
      handleOpenInitialBalanceDialog,
      handleCloseInitialBalanceDialog,
      handleEditInitialBalance,
      handleOpenInitialBalanceDelete,
      handleDeleteInitialBalance,
      handleOpenContractDebtDetail,
      handleOpenCouponDetail,
      handleOpenContractDetail,
      handleRefreshVisibleData,
    },
  };
};

export default usePuntajePage;
